import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "@/auth/AuthProvider";
import { ApiRequestError, get, post } from "@/lib/api";
import { Alert, Button, Card, InputField } from "@/components/ui";
import { ThemeToggle } from "@/components/ThemeToggle";

type Mode = "choose" | "code" | "master";

interface Providers {
  google: boolean;
  email: boolean;
  master: boolean;
}

const OAUTH_ERRORS: Record<string, string> = {
  state_mismatch: "That sign-in link expired or was tampered with. Try again.",
  state_expired: "That sign-in attempt timed out. Try again.",
  google_exchange_failed: "Google could not complete the sign-in. Try again.",
  email_unverified: "Your Google account's email address is not verified.",
  account_suspended: "This account has been suspended.",
  access_denied: "Sign-in was cancelled.",
};

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();

  const redirectTo = params.get("redirectTo") ?? "/dashboard";
  const [mode, setMode] = useState<Mode>("choose");
  const [providers, setProviders] = useState<Providers>({
    google: false,
    email: false,
    master: false,
  });

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() => {
    const raw = params.get("error");
    return raw ? (OAUTH_ERRORS[raw] ?? "Sign-in failed. Try again.") : null;
  });

  useEffect(() => {
    void get<Providers>("/auth/providers").then(setProviders).catch(() => undefined);
  }, []);

  // Already signed in when the page loads? Offer to continue or switch
  // rather than skipping the form — otherwise a stale session pins you to
  // whichever account you last used and Google's chooser is never reached.
  // The ref keeps this card from flashing between a successful submit's
  // refresh() and its explicit navigate().
  const submitting = useRef(false);
  const signedIn = !loading && user !== null && !submitting.current;

  const switchAccount = () => {
    void submit(async () => {
      await post("/auth/logout");
      await refresh();
      submitting.current = false;
    });
  };

  async function submit(fn: () => Promise<void>) {
    setBusy(true);
    submitting.current = true;
    setError(null);
    try {
      await fn();
    } catch (err) {
      submitting.current = false;
      setError(
        err instanceof ApiRequestError ? err.message : "Something went wrong. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  const requestCode = (e: FormEvent) => {
    e.preventDefault();
    void submit(async () => {
      await post("/auth/otp/request", { email: email.trim().toLowerCase() });
      setMode("code");
      setNotice(`If an account exists for ${email.trim()}, a 6-digit code is on its way.`);
    });
  };

  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    void submit(async () => {
      const res = await post<{ redirectTo: string }>("/auth/otp/verify", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        redirectTo,
      });
      await refresh();
      navigate(res.redirectTo, { replace: true });
    });
  };

  const masterLogin = (e: FormEvent) => {
    e.preventDefault();
    void submit(async () => {
      const res = await post<{ redirectTo: string }>("/auth/master", {
        email: email.trim(),
        password,
      });
      await refresh();
      navigate(res.redirectTo, { replace: true });
    });
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-card__brand">
          <Link to="/" style={{ textDecoration: "none" }}>
            <h1 style={{ fontSize: "var(--text-lg)" }}>Lucas Farmer</h1>
          </Link>
          <p className="muted" style={{ fontSize: "var(--text-sm)", marginTop: "var(--s-1)" }}>
            {mode === "master" ? "Administrator access" : "Sign in to continue"}
          </p>
        </div>

        <Card>
          <div className="stack">
            {error ? <Alert tone="error">{error}</Alert> : null}
            {notice && !error ? <Alert tone="info">{notice}</Alert> : null}

            {signedIn && (
              <>
                <p className="muted" style={{ fontSize: "var(--text-sm)" }}>
                  Signed in as <strong>{user.email}</strong>
                </p>
                <Button
                  type="button"
                  variant="primary"
                  block
                  onClick={() => navigate(redirectTo, { replace: true })}
                >
                  Continue
                </Button>
                <Button type="button" variant="ghost" block loading={busy} onClick={switchAccount}>
                  Use a different account
                </Button>
              </>
            )}

            {!signedIn && mode === "choose" && (
              <>
                {providers.google && (
                  <a
                    className="btn btn--secondary btn--block btn--lg"
                    href={`/api/auth/google/start?redirectTo=${encodeURIComponent(redirectTo)}`}
                  >
                    <GoogleMark />
                    Continue with Google
                  </a>
                )}

                {providers.google && providers.email && (
                  <div className="auth-divider">or</div>
                )}

                {/* Hidden unless RESEND_API_KEY is set — otherwise this form
                    would claim to send a code that never arrives. */}
                {providers.email && (
                <form onSubmit={requestCode} className="stack">
                  <InputField
                    label="Email address"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button type="submit" variant="primary" block loading={busy}>
                    Email me a sign-in code
                  </Button>
                </form>
                )}

                {!providers.google && !providers.email && (
                  <Alert tone="info">
                    No sign-in provider is configured yet. Set GOOGLE_CLIENT_ID or
                    RESEND_API_KEY, or use administrator sign-in below.
                  </Alert>
                )}
              </>
            )}

            {mode === "code" && (
              <form onSubmit={verifyCode} className="stack">
                <InputField
                  label="6-digit code"
                  className="input--code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autoFocus
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  hint={`Sent to ${email}. Expires in 10 minutes.`}
                />
                <Button type="submit" variant="primary" block loading={busy}>
                  Sign in
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  block
                  size="sm"
                  onClick={() => {
                    setMode("choose");
                    setCode("");
                    setNotice(null);
                  }}
                >
                  Use a different email
                </Button>
              </form>
            )}

            {mode === "master" && (
              <form onSubmit={masterLogin} className="stack">
                <InputField
                  label="Administrator email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <InputField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button type="submit" variant="primary" block loading={busy}>
                  Sign in
                </Button>
              </form>
            )}
          </div>
        </Card>

        <div
          className="row row--between"
          style={{ marginTop: "var(--s-4)", fontSize: "var(--text-xs)" }}
        >
          {providers.master ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setMode(mode === "master" ? "choose" : "master");
                setError(null);
                setNotice(null);
              }}
            >
              {mode === "master" ? "Back to normal sign-in" : "Administrator sign-in"}
            </button>
          ) : (
            <span />
          )}
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.93v2.33A9 9 0 0 0 9 18Z"/>
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.93a9 9 0 0 0 0 8.1l3.04-2.33Z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.9 11.43 0 9 0A9 9 0 0 0 .93 4.95l3.04 2.33C4.68 5.16 6.66 3.58 9 3.58Z"/>
    </svg>
  );
}
